/* eslint-disable */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import DataTable from "../DataTable/DataTable";
import { getPropertiesList, getStatisticsSuccess } from "../../../actions";
import { history } from "../../../config/stores";
// removed export icon per request
import listActiveIcon from '../../../assets/icons/map/list-active.svg';
import listIcon from '../../../assets/icons/map/list.svg';
import mapIcon from '../../../assets/icons/map/map.svg';
import mapActiveIcon from '../../../assets/icons/map/map-active.svg';
import Header from "../../Header/Header";
import { tablePresets } from "../DataTable/tablePresets";
import * as XLSX from "xlsx";
import moment from "moment";
import { toast } from "react-toastify";
import "./PropertiesList.scss";

const PropertiesList = ({ getPropertiesList, getStatisticsSuccess, propertiesList }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentFilterPage, setCurrentFilterPage] = useState(1); // For client-side pagination of filtered results
  const location = useLocation();

  // Constants
  const pageSize = 20;

  // Parse URL parameters for filtering
  const urlParams = new URLSearchParams(location.search);
  const countryFilter = decodeURIComponent(urlParams.get('country') || ''); // Decode URL encoding
  const cityFilter = decodeURIComponent(urlParams.get('city') || '');       // Decode URL encoding
  const propertyTypeFilter = decodeURIComponent(urlParams.get('propertyType') || ''); // Add property type filter
  const startDateFilter = urlParams.get('startDate');
  const endDateFilter = urlParams.get('endDate');
  const periodLabelFilter = urlParams.get('periodLabel');

  // Check if we have any filters that require loading all data
  const hasFilters = countryFilter || cityFilter || propertyTypeFilter || startDateFilter;

  // Helper function to get country code from country name (reverse lookup)
  const getCountryCodeFromName = (countryName) => {
    const countries = {
      'Bulgaria': 'bg',
      'Greece': 'gr', 
      'Romania': 'ro',
      'Russia': 'ru',
      'United States': 'us',
      'United States of America': 'us', // Add this variant
      'New Zealand': 'nz',
      'Germany': 'de',
      'France': 'fr',
      'Italy': 'it',
      'Spain': 'es',
      'United Kingdom': 'uk',
      'Canada': 'ca',
      'Australia': 'au',
      'Japan': 'jp',
      'South Korea': 'kr',
      'China': 'cn',
      'India': 'in',
      'Brazil': 'br',
      'Mexico': 'mx',
      'Argentina': 'ar',
      'Turkey': 'tr',
      'Poland': 'pl',
      'Netherlands': 'nl',
      'Sweden': 'se',
      'Norway': 'no',
      'Denmark': 'dk',
      'Finland': 'fi',
      'Switzerland': 'ch',
      'Austria': 'at',
      'Belgium': 'be',
      'Portugal': 'pt',
      'Ireland': 'ie',
      'Czech Republic': 'cz',
      'Hungary': 'hu',
      'Slovakia': 'sk',
      'Slovenia': 'si',
      'Croatia': 'hr',
      'Serbia': 'rs',
      'Ukraine': 'ua',
      'Estonia': 'ee',
      'Latvia': 'lv',
      'Lithuania': 'lt'
    };
    return countries[countryName];
  };

  // Filter properties based on URL parameters
  const getFilteredProperties = (properties) => {
    if (!properties || !Array.isArray(properties)) return [];

    return properties.filter(property => {
      if (!property) return false;

      // Country filter - try exact match first, then partial match
      if (countryFilter) {
        const countryName = tablePresets.getCountryName(property.countryCode);
        
        // Try exact match first
        if (countryName === countryFilter) {
          // Exact match - continue
        } 
        // Try partial match (e.g., "United States" matches "United States of America")
        else if (countryName && countryFilter && (
          countryName.toLowerCase().includes(countryFilter.toLowerCase()) ||
          countryFilter.toLowerCase().includes(countryName.toLowerCase())
        )) {
          // Partial match - continue
        }
        // No match
        else {
          return false;
        }
      }

      // City filter
      if (cityFilter) {
        if (property.cityEN !== cityFilter) return false;
      }

      // Property type filter
      if (propertyTypeFilter) {
        if (property.propertyType?.name !== propertyTypeFilter) return false;
      }

      // Period filter (if dates are provided)
      if (startDateFilter && endDateFilter) {
        const propertyDate = new Date(property.createdAt);
        const startDate = new Date(startDateFilter);
        const endDate = new Date(endDateFilter);
        
        if (propertyDate < startDate || propertyDate > endDate) return false;
      }

      return true;
    });
  };

  // Get filtered properties
  const filteredProperties = getFilteredProperties(propertiesList?.docs || []);
  
  // Client-side pagination for filtered results
  const getDisplayProperties = () => {
    if (!hasFilters) {
      // No filters: return all loaded data (server-side pagination)
      return filteredProperties;
    } else {
      // With filters: show accumulated pages of filtered results
      const endIndex = currentFilterPage * pageSize;
      return filteredProperties.slice(0, endIndex); // Show all items from page 1 to current page
    }
  };

  const displayProperties = getDisplayProperties();
  
  // Calculate pagination info for filtered results
  const getDataForTable = () => {
    if (!hasFilters) {
      // No filters: use original server pagination
      return {
        docs: displayProperties.filter(Boolean),
        totalPages: propertiesList?.totalPages || 0,
        page: propertiesList?.page || 1,
        totalDocs: propertiesList?.totalDocs || 0,
        hasNextPage: propertiesList?.hasNextPage || false
      };
    } else {
      // With filters: use client-side pagination
      const totalFilteredPages = Math.ceil(filteredProperties.length / pageSize);
      return {
        docs: displayProperties.filter(Boolean),
        totalPages: totalFilteredPages,
        page: currentFilterPage,
        totalDocs: filteredProperties.length,
        hasNextPage: currentFilterPage < totalFilteredPages
      };
    }
  };
  
  // Generate title with filter information
  const getTitle = () => {
    const filters = [];
    if (countryFilter) filters.push(`in ${countryFilter}`);
    if (cityFilter) filters.push(`in ${cityFilter}`);
    if (propertyTypeFilter) filters.push(`(${propertyTypeFilter})`);
    if (periodLabelFilter && periodLabelFilter !== "All time") filters.push(`(${periodLabelFilter})`);
    
    const baseTitle = "PROPERTIES IN THE APP";
    return filters.length > 0 ? `${baseTitle} ${filters.join(' ')}` : baseTitle;
  };

  useEffect(() => {
    // Set loading state and fetch properties list
    getStatisticsSuccess({ type: "propertiesList", loading: true });
    setCurrentPage(1);
    setCurrentFilterPage(1); // Reset filter pagination when URL changes
    setIsLoading(true);
    
    if (hasFilters) {
      // If we have filters, request ALL data with high limit
      getPropertiesList({
        page: 1,
        limit: 99999, // Very high limit to get all data
        onSuccess: () => {
          setIsLoading(false);
        },
        onError: (error) => {
          setIsLoading(false);
        }
      });
    } else {
      // No filters, load with normal pagination (20 per page)
      getPropertiesList({
        page: 1,
        limit: 20,
        onSuccess: () => setIsLoading(false),
        onError: () => setIsLoading(false)
      });
    }
  }, [hasFilters, countryFilter, cityFilter, propertyTypeFilter, startDateFilter]); // Reload when URL params change

  // Handle Excel export with filtered data
  const handleExport = async () => {
    if (isExporting) return; // Prevent multiple exports
    
    try {
      setIsExporting(true);
      
      let dataToExport;
      
      // If no filters, we need to load ALL data for export
      if (!hasFilters) {
        // Load all data for export (similar to Communities approach)
        try {
          const allPropertiesResponse = await new Promise((resolve, reject) => {
            getPropertiesList({
              page: 1,
              limit: 99999, // Get all properties
              onSuccess: (response) => resolve(response),
              onError: (error) => reject(error)
            });
          });
          
          // Wait a bit for Redux state to update
          await new Promise(resolve => setTimeout(resolve, 500));
          dataToExport = propertiesList?.docs || [];
        } catch (error) {
          console.error('Failed to load all properties for export:', error);
          toast.error('Failed to load all data for export');
          return;
        }
      } else {
        // Use already filtered properties (same data that's displayed in the table)
        dataToExport = filteredProperties;
      }
      
      if (!dataToExport || dataToExport.length === 0) {
        toast.warning('No data to export');
        return;
      }

      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Define headers matching the actual data columns
      const headers = [
        'Country',
        'City',
        'Type',
        'Status',
        'Monthly rent',
        'Shared with users',
        'Total storage',
        'Added on',
        'Added by',
        'Phone number',
        'Role',
        'Property located in',
        'City'
      ];
      
      // Transform data rows - mapping based on actual table data structure
      const rows = dataToExport.map(property => {
        return [
          tablePresets.getCountryName(property.countryCode) || '---',
          property.cityEN || property.city || '---',
          property.propertyType?.name || property.type?.name || property.type || '---',
          // Status mapping - use the same logic as the table
          (() => {
            const rent = property.rent || property.rentAmount;
            const role = property.createdBy?.role;

            // Property with rent = someone is paying rent
            if (rent && rent > 0) {
              // Owner/landlord receiving rent = "Rented out"
              if (role === 'landlord' || role === 'owner') {
                return 'Rented out';
              } else {
                // Other roles paying rent = "Rented" 
                return 'Rented';
              }
            }

            // Property without rent = owned but not rented
            if (!rent || rent === 0 || rent === null) {
              return 'Owned';
            }

            return 'Owned'; // fallback
          })(),
          // Monthly rent - show rent amount with currency or "---" if not available
          (property.rent || property.rentAmount) && (property.rent !== 0 && property.rentAmount !== '0') ? 
            `${property.rent || property.rentAmount} ${property.currency || ''}`.trim() : '---',
          property.usersCount || property.membersCount || property.tenantsCount || property.sharedWithUsers || '1',
          property.storageUsed || property.totalStorage || 'TODO',
          property.createdAt ? new Date(property.createdAt).toLocaleDateString('en-GB').replace(/\//g, '.') : '---',
          property.createdBy ? (property.createdBy.fullName || property.createdBy.email || property.createdBy.name || '---') : 
            property.owner ? (property.owner.fullName || property.owner.email || property.owner.name || '---') : '---',
          property.createdBy?.phoneNumber || property.createdBy?.phone || 
            property.owner?.phoneNumber || property.owner?.phone || property.phoneNumber || property.phone || 'TODO',
          property.createdBy?.role || property.owner?.role || property.role || property.userRole || 
            (property.isLandlord ? 'Landlord' : (property.isOwner ? 'Owner' : 'Unknown')) || '---',
          // Property located in - use country name (same as first column)
          tablePresets.getCountryName(property.countryCode) || '---',
          property.cityEN || property.city || '---' // Duplicate city column as requested
        ];
      });
      
      // Create worksheet
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([headers, ...rows]),
        "Properties"
      );
      
      // Generate filename with current date and filter info
      let filename = `properties-${moment().format("DD-MM-YYYY")}`;
      
      if (countryFilter || cityFilter || propertyTypeFilter || periodLabelFilter) {
        filename += '-filtered';
        if (countryFilter) {
          filename += `-${countryFilter.replace(/\s+/g, '-').toLowerCase()}`;
        }
        if (cityFilter) {
          filename += `-${cityFilter.replace(/\s+/g, '-').toLowerCase()}`;
        }
        if (propertyTypeFilter) {
          filename += `-${propertyTypeFilter.replace(/\s+/g, '-').toLowerCase()}`;
        }
        if (periodLabelFilter && periodLabelFilter !== "All time") {
          filename += `-${periodLabelFilter.replace(/\s+/g, '-').toLowerCase()}`;
        }
      }
      
      filename += '.xlsx';
      
      // Download file
      XLSX.writeFile(wb, filename);
      
      // Show success message
      toast.success(`Exported ${rows.length} properties to ${filename}`);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleLoadMore = () => {
    if (hasFilters) {
      // Client-side pagination for filtered results
      const totalFilteredPages = Math.ceil(filteredProperties.length / pageSize);
      
      if (currentFilterPage < totalFilteredPages) {
        setCurrentFilterPage(prev => prev + 1);
      }
    } else {
      // Server-side pagination for non-filtered results
      if (propertiesList?.hasNextPage && !isLoading) {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        setIsLoading(true);
        
        getPropertiesList({
          page: nextPage,
          limit: 20,
          onSuccess: (response) => {
            setIsLoading(false);
          },
          onError: (error) => {
            setIsLoading(false);
            // Reset to previous page on error
            setCurrentPage(currentPage);
          }
        });
      }
    }
  };

  const handleBackToDashboard = () => {
    history.push('/');
  };

  return (
    <>
      <Header />
      <div className="main-container">
        <div className="inner-header-container">
          <div className="left-part">
            <div 
              className="close-icon" 
              onClick={handleBackToDashboard}
              title="Back to Dashboard"
            >
              ×
            </div>
            <div className="inner-title">{getTitle()}</div>
          </div>
          <div className="right-part">
            <div className="view-switch" aria-label="Switch to map view">
              <button
                type="button"
                className="view-icon active"
                title="List view"
                aria-label="List view (active)"
              >
                <img src={listActiveIcon} alt="List active" width={22} height={22} />
              </button>
              <button
                type="button"
                className="view-icon"
                title="Open map view"
                aria-label="Open map view"
                onClick={() => history.push(`/dashboard/properties/map${location.search || ''}`)}
              >
                <img src={mapIcon} alt="Map" width={22} height={22} />
              </button>
            </div>
            {(countryFilter || cityFilter || propertyTypeFilter) && (
              <div className="filter-info">
                <span className="filter-label">FILTERED BY:</span>
                <span className="filter-value">
                  {countryFilter && `COUNTRY - ${countryFilter.toUpperCase()}`}
                  {countryFilter && (cityFilter || propertyTypeFilter) && ' | '}
                  {cityFilter && `CITY - ${cityFilter.toUpperCase()}`}
                  {cityFilter && propertyTypeFilter && ' | '}
                  {propertyTypeFilter && `TYPE - ${propertyTypeFilter.toUpperCase()}`}
                </span>
              </div>
            )}
            {/* export icon removed */}
          </div>
        </div>
        <div className="body-container">
          <DataTable
            data={getDataForTable()}
            preset="propertiesList"
            loading={propertiesList?.loading || isLoading}
            onLoadMore={handleLoadMore}
            emptyStateText={
              (countryFilter || cityFilter || propertyTypeFilter) 
                ? `No properties found ${countryFilter ? `in ${countryFilter}` : ''} ${cityFilter ? `in ${cityFilter}` : ''} ${propertyTypeFilter ? `of type ${propertyTypeFilter}` : ''}`.trim()
                : "No properties found"
            }
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  propertiesList: state.dashboard.propertiesList,
});

const mapDispatchToProps = (dispatch) => ({
  getPropertiesList: (payload) => dispatch(getPropertiesList(payload)),
  getStatisticsSuccess: (payload) => dispatch(getStatisticsSuccess(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PropertiesList);